import { FC } from 'react';
import { ComponentRendering, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { sanitize } from 'isomorphic-dompurify';

import * as icons from '@gn-audio/icons';
import {
  Button,
  ComponentProps,
  isColorBright,
  JabraIcon,
  resolveColorProperty,
} from '@gn-audio/jss-components';

import { Eyebrow } from 'common/components/Eyebrow';
import { Disclaimer } from 'components/Product/StatsSpot/components/Disclaimer';
import { Stat } from 'components/Product/StatsSpot/components/Stat';
import { StatsSpotFields } from 'components/Product/StatsSpot/statsSpotTypes';

export type StatsSpotProps = {
  fields: StatsSpotFields;
} & ComponentProps<StatsSpotFields>;

const StatsSpot: FC<StatsSpotProps> = ({ fields }) => {
  const {
    backgroundColor,
    eyebrowText,
    headline,
    description,
    stats,
    footerText,
    ctaButtonLink,
    ctaButtonLabel,
    ctaButtonAriaLabel,
    disclaimers,
  } = fields;

  const resolvedBackgroundColor = resolveColorProperty(backgroundColor?.value);
  const isBackgroundBright = isColorBright(resolvedBackgroundColor);
  const textColorClass = isBackgroundBright ? 'text-black' : 'text-white';
  const themeClass = isBackgroundBright ? 'light' : 'dark';

  return (
    <article style={{ backgroundColor: resolvedBackgroundColor }} className={themeClass}>
      <div
        className={classNames(
          'container flex w-full flex-col items-center justify-center md:w-3/4 lg:w-2/3',
          textColorClass
        )}
      >
        <header>
          {!!eyebrowText?.value && (
            <Eyebrow align="center" text={eyebrowText.value} className="mb-12" />
          )}
          {!!headline?.value && <h2 className="heading-2 mb-6 text-center">{headline.value}</h2>}
          {!!description?.value && (
            <p
              className="paragraph-large mb-6 text-center md:mb-12"
              dangerouslySetInnerHTML={{ __html: description.value }}
            />
          )}
        </header>
        {!!stats?.length && (
          <section className="mb-6 flex flex-col gap-3 md:gap-6">
            {stats.map((stat) => (
              <div key={stat.id}>
                <Stat
                  value={stat.value}
                  valueFontColor={stat.valueFontColor}
                  description={stat.description}
                />
              </div>
            ))}
          </section>
        )}
        <footer className="flex flex-col items-center justify-center">
          {!!footerText?.value && (
            <p
              className="paragraph-large mb-12 text-center"
              dangerouslySetInnerHTML={{ __html: footerText.value }}
            />
          )}
          {!!ctaButtonLink?.value?.href && (
            <Button
              size="l"
              asLink
              variant="secondary"
              iconPlacement="right"
              className="mb-12 inline-flex"
              href={ctaButtonLink.value.href}
              icon={<JabraIcon Icon={icons.icon_arrow_right} />}
              {...(ctaButtonAriaLabel?.value && {
                'aria-label': ctaButtonAriaLabel.value,
              })}
            >
              {ctaButtonLabel?.value}
            </Button>
          )}
          {!!disclaimers.length && (
            <div className="flex flex-row flex-wrap items-center justify-center gap-2">
              {disclaimers.map((disclaimer) => (
                <Disclaimer text={disclaimer.text} key={disclaimer.id} />
              ))}
            </div>
          )}
        </footer>
      </div>
    </article>
  );
};

export const getStaticProps = async (rendering: ComponentRendering<StatsSpotFields>) => {
  try {
    const rawFields = rendering.fields;
    if (!rawFields) {
      throw new Error('No fields found for Stats Spot');
    }
    const fields = {
      ...rawFields,
      description: {
        ...rawFields.description,
        value: sanitize(rawFields.description?.value),
      },
      disclaimers: rawFields.disclaimers.map((disclaimer) => ({
        ...disclaimer,
        disclaimerText: {
          ...disclaimer.text,
          value: sanitize(disclaimer.text?.value),
        },
      })),
      footerText: { ...rawFields.footerText, value: sanitize(rawFields.footerText?.value) },
      stats: rawFields.stats.map((stat) => ({
        ...stat,
        statDescription: { ...stat.description, value: sanitize(stat.description?.value) },
      })),
    };

    return { fields };
  } catch (error) {
    console.log(error);
    const { params = {} } = rendering;
    return { params };
  }
};

export default withDatasourceCheck()(StatsSpot);
